import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Wand2, Download, FileText, Table, Loader2, Plus, Trash2, ArrowUp, ArrowDown, ArrowUpDown, AlertTriangle } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { startBatchAnalysis, getAnalysis } from '../api/analysis'
import AnalysisJobsPanel from '../components/AnalysisJobsPanel'
import AnalysisSourcePicker, { type AnalysisSource } from '../components/AnalysisSourcePicker'
import { useSearchFilters } from '../contexts/SearchFiltersContext'
import Popover from '../components/Popover'
import BookmarkStar from '../components/BookmarkStar'

interface ColumnTemplate {
  name: string
  instruction: string
}

interface AIResult {
  output_format: string
  n_papers_analyzed: number
  batches: number
  text?: string
  table_data?: any[]
  raw_text?: string
}

export default function AIAnalysisPage() {
  const [searchParams] = useSearchParams()
  const searchId = searchParams.get('search_id')
  
  const [question, setQuestion] = useState('')
  const [outputFormat, setOutputFormat] = useState<'text' | 'table'>('text')
  const [maxRows, setMaxRows] = useState(500)
  const [rowSelection, setRowSelection] = useState<'top' | 'random'>('top')
  const [detailLevel, setDetailLevel] = useState<'normal' | 'detailed'>('normal')
  
  // Table template state
  const [columns, setColumns] = useState<ColumnTemplate[]>([
    { name: 'PMID', instruction: 'The PubMed ID of the article' },
    { name: 'Key Finding', instruction: 'Summarize the main finding of this paper in 1-2 sentences' },
  ])
  
  const [status, setStatus] = useState<'idle' | 'running' | 'complete' | 'error'>('idle')
  const [result, setResult] = useState<AIResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [limitModalOpen, setLimitModalOpen] = useState(false)
  const [limitMessage, setLimitMessage] = useState('')

  // Persist the most recent analysis_id per search_id so results survive
  // navigation away and back.
  const storageKey = searchId ? `ai_analysis_${searchId}` : null
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const queryClient = useQueryClient()
  const { loadedDatasetId, filteredPmids } = useSearchFilters()
  const [source, setSource] = useState<AnalysisSource>(
    { type: 'search', searchId: searchId || null }
  )

  const pollAnalysis = (analysisId: string) => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(async () => {
      try {
        const analysis = await getAnalysis(analysisId)
        const prog = (analysis.results as any)?.progress
        if (prog && typeof prog.done === 'number' && typeof prog.total === 'number') {
          setProgress({ done: prog.done, total: prog.total })
        }
        if (analysis.status === 'complete' && analysis.results) {
          if (pollRef.current) clearInterval(pollRef.current)
          setResult(analysis.results as unknown as AIResult)
          setStatus('complete')
          setProgress(null)
        } else if (analysis.status === 'error') {
          if (pollRef.current) clearInterval(pollRef.current)
          setError(analysis.error_message || 'Analysis failed')
          setStatus('error')
          setProgress(null)
        }
      } catch (err: any) {
        console.error('Polling error:', err)
      }
    }, 3000)
    setTimeout(() => {
      if (pollRef.current) clearInterval(pollRef.current)
    }, 600000)
  }

  // On mount / search switch: restore the last analysis for this searchId.
  useEffect(() => {
    if (!storageKey) return
    const savedId = localStorage.getItem(storageKey)
    if (!savedId) return
    getAnalysis(savedId).then((analysis) => {
      if (analysis.status === 'complete' && analysis.results) {
        setResult(analysis.results as unknown as AIResult)
        setStatus('complete')
      } else if (analysis.status === 'running') {
        setStatus('running')
        pollAnalysis(savedId)
      } else if (analysis.status === 'error') {
        setStatus('error')
        setError(analysis.error_message || 'Analysis failed')
      }
    }).catch(() => {
      // Stale ID (analysis deleted) — clear it
      localStorage.removeItem(storageKey)
    })
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [storageKey])

  const addColumn = () => {
    setColumns([...columns, { name: '', instruction: '' }])
  }

  const removeColumn = (index: number) => {
    if (index === 0) return // Can't remove PMID column
    setColumns(columns.filter((_, i) => i !== index))
  }

  const updateColumn = (index: number, field: 'name' | 'instruction', value: string) => {
    const updated = [...columns]
    updated[index] = { ...updated[index], [field]: value }
    setColumns(updated)
  }

  const startAnalysis = async () => {
    if (!searchId) {
      setError('No search selected')
      return
    }
    
    // Question is required for text output, optional for table output with template
    const hasTableTemplate = outputFormat === 'table' && columns.length > 1
    if (!question.trim() && !hasTableTemplate) {
      setError('Please enter a question')
      return
    }
    
    if (outputFormat === 'table') {
      // Validate columns
      const invalidColumns = columns.filter((c, i) => i > 0 && (!c.name.trim() || !c.instruction.trim()))
      if (invalidColumns.length > 0) {
        setError('All columns must have a name and extraction instruction')
        return
      }
    }

    setStatus('running')
    setError(null)
    setResult(null)
    setProgress(null)

    try {
      const params: any = {
        question: question.trim(),
        output_format: outputFormat,
        max_rows: maxRows,
        row_selection: rowSelection,
        detail_level: detailLevel,
      }
      if (source.type === 'search') {
        params.search_id = source.searchId || searchId
        if (loadedDatasetId) params.dataset_id = loadedDatasetId
        // Pass current filtered PMIDs so the analysis runs on what the user sees.
        const fp = source.filteredPmids ?? (filteredPmids.size > 0 ? Array.from(filteredPmids) : undefined)
        if (fp && fp.length > 0) params.pmids = fp
      } else if (source.type === 'dataset') {
        params.dataset_id = source.datasetId
      } else if (source.type === 'bookmark_folder') {
        params.bookmark_folder_id = source.folderId
      }
      
      if (outputFormat === 'table') {
        params.table_template = columns.map(c => ({
          name: c.name,
          instruction: c.instruction
        }))
      }
      
      const response = await startBatchAnalysis(params)
      if (storageKey) localStorage.setItem(storageKey, response.analysis_id)
      queryClient.invalidateQueries({ queryKey: ['analyses-list', 'ai_batch'] })
      pollAnalysis(response.analysis_id)
    } catch (err: any) {
      setStatus('error')
      const msg = err.response?.data?.detail || err.message
      if (err.response?.status === 429) {
        setLimitMessage(msg)
        setLimitModalOpen(true)
      } else {
        setError(msg)
      }
    }
  }

  const loadFromJob = async (id: string) => {
    setStatus('running')
    setError(null)
    setResult(null)
    setProgress(null)
    try {
      const analysis = await getAnalysis(id)
      if (analysis.status === 'complete' && analysis.results) {
        setResult(analysis.results as unknown as AIResult)
        setStatus('complete')
      } else if (analysis.status === 'error') {
        setError(analysis.error_message || 'Analysis failed')
        setStatus('error')
      } else if (analysis.status === 'cancelled') {
        setError('Analysis was cancelled')
        setStatus('error')
      } else {
        // Re-poll if still running.
        pollAnalysis(id)
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load')
      setStatus('error')
    }
  }

  const downloadText = () => {
    if (!result?.text) return
    const blob = new Blob([result.text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ai_analysis_${new Date().toISOString().split('T')[0]}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  const downloadCSV = () => {
    if (!result?.table_data) return
    const headers = Object.keys(result.table_data[0] || {})
    const csv = [
      headers.join(','),
      ...result.table_data.map((row: any) => 
        headers.map(h => {
          const val = row[h] || ''
          return `"${String(val).replace(/"/g, '""')}"`
        }).join(',')
      )
    ].join('\n')
    
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ai_analysis_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (!searchId) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-2xl font-bold mb-4">AI Analysis</h2>
        <p className="text-gray-600">No search selected. Go to Search and perform a search first.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-blue-800">
          Ask questions about your search results using AI. The LLM will analyze the papers and provide insights.
        </p>
      </div>

      <AnalysisJobsPanel type="ai_batch" onLoad={loadFromJob} title="AI analyses" />

      <AnalysisSourcePicker
        value={source}
        onChange={setSource}
        searchId={searchId}
        currentFilteredPmids={filteredPmids.size > 0 ? Array.from(filteredPmids) : undefined}
        allowedSources={['dataset', 'bookmark_folder']}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Panel */}
        <div className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
          <h3 className="text-lg font-semibold">Settings</h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Output Format</label>
            <div className="flex gap-2">
              <button
                onClick={() => setOutputFormat('text')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm ${
                  outputFormat === 'text'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <FileText className="w-4 h-4" />
                Text
              </button>
              <button
                onClick={() => setOutputFormat('table')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm ${
                  outputFormat === 'table'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Table className="w-4 h-4" />
                Table
              </button>
            </div>
          </div>

          {outputFormat === 'table' && (
            <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-700">Table Template Builder</h4>
                <span className="text-xs text-gray-500">PMID is always included</span>
              </div>
              
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {columns.map((col, index) => (
                  <div key={index} className="bg-white rounded-md p-3 border space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 w-16">Column {index + 1}</span>
                      <input
                        type="text"
                        value={col.name}
                        onChange={(e) => updateColumn(index, 'name', e.target.value)}
                        placeholder="Column name"
                        disabled={index === 0}
                        className="flex-1 text-sm border rounded px-2 py-1 disabled:bg-gray-100"
                      />
                      {index > 0 && (
                        <button
                          onClick={() => removeColumn(index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                          title="Remove column"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <textarea
                      value={col.instruction}
                      onChange={(e) => updateColumn(index, 'instruction', e.target.value)}
                      placeholder="What information should be extracted for this column? Be specific."
                      rows={2}
                      disabled={index === 0}
                      className="w-full text-xs border rounded px-2 py-1 resize-none disabled:bg-gray-100"
                    />
                  </div>
                ))}
              </div>
              
              <button
                onClick={addColumn}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-md text-sm text-gray-600 hover:bg-white hover:border-blue-400 hover:text-blue-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Column
              </button>
              
              <div className="text-xs text-gray-500 bg-yellow-50 p-2 rounded border border-yellow-200">
                <strong>Tip:</strong> Be specific in your instructions. For example: "When relevant, list the key protein biomarkers identified for Parkinson's disease (provide just protein names, nothing else)."
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Rows: <span className="font-bold text-blue-600">{maxRows}</span>
            </label>
            <input
              type="range"
              min={50}
              max={2000}
              step={50}
              value={maxRows}
              onChange={(e) => setMaxRows(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Selection</label>
            <div className="flex gap-2">
              <button
                onClick={() => setRowSelection('top')}
                className={`flex-1 px-3 py-2 rounded-md text-sm ${
                  rowSelection === 'top'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Top rows
              </button>
              <button
                onClick={() => setRowSelection('random')}
                className={`flex-1 px-3 py-2 rounded-md text-sm ${
                  rowSelection === 'random'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Random sample
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Detail Level</label>
            <div className="flex gap-2">
              <button
                onClick={() => setDetailLevel('normal')}
                className={`flex-1 px-3 py-2 rounded-md text-sm ${
                  detailLevel === 'normal'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Normal
              </button>
              <button
                onClick={() => setDetailLevel('detailed')}
                className={`flex-1 px-3 py-2 rounded-md text-sm ${
                  detailLevel === 'detailed'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Detailed
              </button>
            </div>
          </div>
        </div>

        {/* Question & Results Panel */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Question {outputFormat === 'table' && <span className="text-gray-400 font-normal">(optional with table template)</span>}
            </label>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={outputFormat === 'table' 
                ? "Optional: Add context or specific focus for the table extraction (e.g., 'Focus on Parkinson's disease biomarkers only')"
                : "e.g., What are the main findings? What methodologies are used? What are the clinical implications?"
              }
              rows={4}
              className="w-full border rounded-md px-3 py-2 resize-none"
            />
            
            <button
              onClick={startAnalysis}
              disabled={status === 'running' || (!question.trim() && outputFormat !== 'table')}
              className="mt-4 w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === 'running' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {progress
                    ? `Batch ${progress.done} / ${progress.total}…`
                    : 'Analyzing… This may take a few minutes.'}
                </>
              ) : (
                <>
                  <Wand2 className="w-5 h-5" />
                  Analyze
                </>
              )}
            </button>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                {error}
              </div>
            )}
          </div>

          {/* Results */}
          {result && status === 'complete' && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">Results</h3>
                  <p className="text-sm text-gray-600">
                    {result.n_papers_analyzed} papers analyzed in {result.batches} batch{result.batches > 1 ? 'es' : ''}
                  </p>
                </div>
                <div className="flex gap-2">
                  {result.output_format === 'text' && result.text && (
                    <button
                      onClick={downloadText}
                      className="flex items-center gap-2 px-4 py-2 border rounded-md text-sm hover:bg-gray-50"
                    >
                      <Download className="w-4 h-4" />
                      Download TXT
                    </button>
                  )}
                  {result.output_format === 'table' && result.table_data && (
                    <button
                      onClick={downloadCSV}
                      className="flex items-center gap-2 px-4 py-2 border rounded-md text-sm hover:bg-gray-50"
                    >
                      <Download className="w-4 h-4" />
                      Download CSV
                    </button>
                  )}
                </div>
              </div>

              {result.output_format === 'text' && result.text && (
                <div className="bg-gray-50 rounded-lg p-4 border max-h-[600px] overflow-y-auto">
                  <div className="prose prose-sm max-w-none">
                    <ReactMarkdown
                      components={{
                        a: ({ node, children, href, ...props }: any) => {
                          // Check if this is a PubMed link
                          const pmidMatch = href?.match(/pubmed\.ncbi\.nlm\.nih\.gov\/(\d+)/)
                          if (pmidMatch) {
                            return (
                              <span className="inline-flex items-center gap-1">
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline font-mono text-xs"
                                  {...props}
                                >
                                  {children}
                                </a>
                                <BookmarkStar pmid={pmidMatch[1]} />
                              </span>
                            )
                          }
                          return (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                              {...props}
                            >
                              {children}
                            </a>
                          )
                        }
                      }}
                    >
                      {result.text}
                    </ReactMarkdown>
                  </div>
                </div>
              )}

              {result.output_format === 'table' && result.table_data && (
                <SortableResultTable rows={result.table_data} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Limit exceeded modal */}
      <Popover open={limitModalOpen} onClose={() => setLimitModalOpen(false)} width={400}>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h3 className="text-lg font-semibold text-gray-900">AI Limit Reached</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">{limitMessage}</p>
          <p className="text-sm text-gray-500 mb-4">
            You can still view your existing analyses and chat history, but you cannot start new AI jobs until your limit resets.
          </p>
          <button
            onClick={() => setLimitModalOpen(false)}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Got it
          </button>
        </div>
      </Popover>
    </div>
  )
}

function isPmidHeader(h: string): boolean {
  return h.trim().toLowerCase() === 'pmid'
}

function compareValues(a: any, b: any): number {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  if (typeof a === 'boolean' && typeof b === 'boolean') return a === b ? 0 : a ? -1 : 1
  // Try numeric coercion (handles "123" vs "45")
  const na = Number(a)
  const nb = Number(b)
  if (!Number.isNaN(na) && !Number.isNaN(nb) && isFinite(na) && isFinite(nb) && String(a).trim() !== '' && String(b).trim() !== '') {
    return na - nb
  }
  return String(a).localeCompare(String(b))
}

function SortableResultTable({ rows }: { rows: any[] }) {
  const headers = Object.keys(rows[0] || {})
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const sortedRows = (() => {
    if (!sortKey) return rows
    const dir = sortDir === 'asc' ? 1 : -1
    return [...rows].sort((r1, r2) => compareValues(r1[sortKey], r2[sortKey]) * dir)
  })()

  const onHeaderClick = (h: string) => {
    if (sortKey === h) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(h)
      setSortDir('asc')
    }
  }

  return (
    <div className="overflow-x-auto max-h-[600px] overflow-y-auto border border-gray-200 rounded-md">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="px-2 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-10">
              ★
            </th>
            {headers.map((header) => {
              const active = sortKey === header
              return (
                <th
                  key={header}
                  onClick={() => onHeaderClick(header)}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100"
                >
                  <span className="inline-flex items-center gap-1">
                    {header}
                    {active ? (
                      sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 text-gray-300" />
                    )}
                  </span>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedRows.map((row: any, idx: number) => (
            <tr key={idx} className="hover:bg-gray-50">
              <td className="px-2 py-3 align-top">
                {row.PMID && String(row.PMID).trim() !== '' ? (
                  <BookmarkStar pmid={String(row.PMID).trim()} />
                ) : null}
              </td>
              {headers.map((header, vIdx) => {
                const val = row[header]
                let cell: React.ReactNode
                if (isPmidHeader(header) && val != null && String(val).trim() !== '') {
                  cell = (
                    <span className="inline-flex items-center gap-1">
                      <a
                        href={`https://pubmed.ncbi.nlm.nih.gov/${String(val).trim()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline font-mono"
                      >
                        {String(val)}
                      </a>
                      <BookmarkStar pmid={String(val).trim()} />
                    </span>
                  )
                } else if (typeof val === 'boolean') {
                  cell = val ? 'Yes' : 'No'
                } else if (val == null) {
                  cell = <span className="text-gray-400">—</span>
                } else {
                  cell = String(val)
                }
                return (
                  <td key={vIdx} className="px-4 py-3 text-sm text-gray-900 align-top">
                    {cell}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
